import React, { lazy } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHeader } from '../components/PageHeader';
import { AboutSection } from '../components/AboutSection';
import { CoreValuesSection } from '../components/CoreValuesSection';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Award, Heart, Users, Target, Quote } from 'lucide-react';
import { SEO } from '../components/SEO';
export function AboutPage() {
  return <div className="font-sans bg-warm-ivory min-h-screen w-full" data-id="element-418">
      <SEO title="About Us - Senator Fatimat Raji Rasaki Foundation" description="Learn about SFRR Foundation's mission, vision, and values. Discover how we're improving lives and strengthening communities across Nigeria through sustainable development." keywords="SFRR Foundation about, Senator Fatimat Raji Rasaki, Nigerian nonprofit mission, community development Nigeria, humanitarian organization" data-id="element-419" />

      <Header data-id="element-420" />
      <main className="w-full font-sans" data-id="element-421">
        <PageHeader title="About Our Foundation" subtitle="Learn about our mission, vision, and the values that drive our work" badgeText="ABOUT US" backgroundImage="https://cdn.magicpatterns.com/uploads/ebg69ikW9uruxw2VgRQH1C/575d5148-67b3-4125-b7ee-4dd154991d71.jpg" data-id="element-422" />

        <AboutSection data-id="element-423" />

        {/* Founder's Vision Section */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-white to-secondary/5 overflow-hidden" data-id="element-424">
          <div className="container mx-auto px-4" data-id="element-425">
            <div className="max-w-6xl mx-auto" data-id="element-426">
              <div className="text-center mb-16" data-id="element-427">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-428">
                  Our Founder
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal mb-6" data-id="element-429">
                  A Legacy of Service & Compassion
                </h2>
                <p className="text-lg text-neutral-600 max-w-3xl mx-auto leading-relaxed" data-id="element-430">
                  Senator Fatimat Raji Rasaki's dedication to uplifting
                  communities spans decades of public service and advocacy.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-431">
                {/* Image */}
                <div className="relative group" data-id="element-432">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" data-id="element-433" />
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-434">
                    <img src="https://cdn.magicpatterns.com/uploads/ebg69ikW9uruxw2VgRQH1C/575d5148-67b3-4125-b7ee-4dd154991d71.jpg" alt="Senator Fatimat Raji Rasaki speaking at community event" className="w-full h-[500px] object-cover object-center transform group-hover:scale-105 transition-transform duration-700" loading="lazy" width="600" height="500" data-id="element-435" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" data-id="element-436" />
                  </div>
                </div>

                {/* Quote & Bio */}
                <div className="space-y-6" data-id="element-437">
                  <Quote className="w-12 h-12 text-primary opacity-50" data-id="element-438" />
                  <blockquote className="text-2xl md:text-3xl font-serif text-charcoal leading-tight" data-id="element-439">
                    "I dedicate my next chapter to structured
                    service—transforming lives through compassion, education,
                    and sustainable development."
                  </blockquote>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-440">
                    Senator Fatimat Raji Rasaki brings decades of legislative
                    experience and grassroots advocacy to the foundation. Her
                    vision is rooted in dignity, empathy, and the belief that
                    every Nigerian deserves access to opportunities that enable
                    them to thrive.
                  </p>
                  <div className="flex flex-wrap gap-3 pt-4" data-id="element-441">
                    <div className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium" data-id="element-442">
                      Former Senator
                    </div>
                    <div className="px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm font-medium" data-id="element-443">
                      Community Advocate
                    </div>
                    <div className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium" data-id="element-444">
                      Humanitarian
                    </div>
                  </div>
                </div>
              </div>

              {/* Mission Pillars */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-445">
                {[{
                icon: <Award className="w-8 h-8" data-id="element-446" />,
                title: 'Excellence',
                description: 'Committed to the highest standards in all our programs',
                color: 'bg-accent'
              }, {
                icon: <Heart className="w-8 h-8" data-id="element-447" />,
                title: 'Compassion',
                description: 'Leading with empathy and understanding',
                color: 'bg-primary'
              }, {
                icon: <Users className="w-8 h-8" data-id="element-448" />,
                title: 'Community',
                description: 'Building stronger, more resilient communities',
                color: 'bg-secondary'
              }, {
                icon: <Target className="w-8 h-8" data-id="element-449" />,
                title: 'Impact',
                description: 'Creating measurable, lasting change',
                color: 'bg-primary'
              }].map((pillar, index) => <div key={index} className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1 group" data-id="element-450">
                    <div className={`w-14 h-14 rounded-xl ${pillar.color} bg-opacity-10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`} data-id="element-451">
                      <div className={`${pillar.color.replace('bg-', 'text-')}`} data-id="element-452">
                        {pillar.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-453">
                      {pillar.title}
                    </h3>
                    <p className="text-neutral-600 text-sm leading-relaxed" data-id="element-454">
                      {pillar.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        <CoreValuesSection data-id="element-455" />

        {/* Team Section */}
        <section className="py-16 bg-white" data-id="element-456">
          <div className="container mx-auto px-4" data-id="element-457">
            <div className="text-center mb-12" data-id="element-458">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-459">
                Our Leadership
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal mb-4" data-id="element-460">
                Meet Our Team
              </h2>
              <p className="max-w-2xl mx-auto text-neutral-600" data-id="element-461">
                Our dedicated team of professionals and volunteers work
                tirelessly to ensure the success of our programs and
                initiatives.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-462">
              <Link to="/structure" data-id="element-463">
                <Button variant="primary" size="lg" data-id="element-464">
                  View Our Organizational Structure
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-465" />
    </div>;
}